import { Component, type ReactNode } from 'react';

import { createPopper } from '@popperjs/core';
import { Portal } from '@skyscanner-internal/backpack-web/bpk-react-utils';

type Nullable<T> = T | null;

type Props = {
  placement?: 'top' | 'right' | 'bottom' | 'left' | 'auto';
  portalClassName?: string;
  target: ReactNode;
  children: ReactNode;
  renderTarget: Nullable<() => Nullable<HTMLElement>>;
};

type State = {
  isOpen: boolean;
  isChildHover: boolean;
};

const defaultProps = { placement: 'bottom', portalClassName: null };

class MapTooltip extends Component<Props, State> {
  popper: any;

  targetRef: any;

  static defaultProps = defaultProps;

  hideTimeout: Nullable<ReturnType<typeof setTimeout>>;

  constructor(props: Props) {
    super(props);

    this.popper = null;
    this.targetRef = null;

    this.state = {
      isOpen: false,
      isChildHover: false,
    };
    this.hideTimeout = null;
  }

  componentDidMount() {
    if (this.targetRef) {
      const ref = this.targetRef;

      ref.addEventListener('mouseenter', this.onOpenTooltip);
      ref.addEventListener('mouseleave', this.onCloseTooltip);
    }
  }

  componentWillUnmount() {
    if (this.targetRef) {
      const ref = this.targetRef;

      ref.removeEventListener('mouseenter', this.onOpenTooltip);
      ref.removeEventListener('mouseleave', this.onCloseTooltip);
    }
  }

  onOpenTooltip = () => {
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }
    this.setState({ isOpen: true });
  };

  onCloseTooltip = () => {
    // Make sure user can move the mouse to the tooltip before it closes
    this.hideTimeout = setTimeout(() => {
      this.setState({
        isOpen: false,
      });
    }, 100);
  };

  onChildMouseEnter = () => {
    this.setState({
      isChildHover: true,
    });
  };

  onChildMouseLeave = () => {
    this.setState({
      isChildHover: false,
    });
  };

  onOpen = (tooltipElement: HTMLElement, targetElement: HTMLElement) => {
    // Note that GPU acceleration should be disabled otherwise Popper will use `translate3d`
    // which can cause blurriness in Safari and Chrome.
    const stdModifiers = [
      {
        name: 'computeStyles',
        options: {
          gpuAcceleration: false,
        },
      },
    ];

    this.popper = createPopper(targetElement, tooltipElement, {
      placement: this.props.placement,
      modifiers: stdModifiers,
    });

    this.popper.update();
  };

  beforeClose = (done: Function) => {
    if (this.popper) {
      this.popper.destroy();
      this.popper = null;
    }

    done();
  };

  render() {
    const { children, portalClassName, renderTarget, target } = this.props;

    return (
      <>
        {target}
        <Portal
          beforeClose={this.beforeClose}
          target={target}
          targetRef={(targetRef: any) => {
            this.targetRef = targetRef;
          }}
          isOpen={this.state.isOpen || this.state.isChildHover}
          onOpen={this.onOpen}
          onClose={this.onCloseTooltip}
          className={portalClassName}
          renderTarget={renderTarget}
        >
          <div
            onMouseEnter={this.onChildMouseEnter}
            onMouseLeave={this.onChildMouseLeave}
          >
            {children}
          </div>
        </Portal>
      </>
    );
  }
}

export default MapTooltip;
